import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Wallet, Sparkles, ArrowRight, Loader2, Check, AlertTriangle,
  CreditCard, History, Zap, ArrowUpRight, Info, Clock,
  Camera, FileText, Code2, Receipt,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Stat, SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Input';
import { Dialog, EmptyState, Skeleton, Spinner } from '../components/Dialog';
import { CheckoutDialog } from '../components/CheckoutDialog';
import { LoadMore } from '../components/LoadMore';
import { apiFetch } from '../api';
import useLoadMore from '../hooks/useLoadMore';
import {
  cn, formatCompact, formatNumber, formatRelative, formatUSD,
} from '../lib/utils';

/**
 * TopupPage — buy credits without changing your subscription.
 *
 * Sliders are in CREDITS (not dollars). The slider's max equals the
 * plan's per-cycle grant (topup_max_credits from /v1/billing); its min
 * is whatever number of credits produces the $15 NOWPayments minimum
 * at the plan's overage rate.
 *
 * Eligibility (server-authoritative, mirrored here for UX):
 *   - free plan:    not eligible (App.jsx redirects to /billing)
 *   - paid, bal>0:  not eligible — slider locked, banner explains
 *   - paid, bal=0:  eligible — slider enabled, checkout works
 *
 * Layout:
 *   1. Gate banner — server-driven eligibility status
 *   2. Slider form — pick a credit amount, see $ cost
 *   3. Balance + per-call pricing sidebar
 *   4. Topup history — last credit purchases
 */

const NOWPAYMENTS_MIN_USD_CENTS = 1500; // $15 — server-side floor

function deriveQuickCreditPresets(maxCredits) {
  // Tiered credit presets, capped to the user's plan max.
  // Picked so users see relevant defaults for any plan (max=200 .. max=400k).
  const tiers = [1000, 5000, 10000, 25000, 50000, 100000];
  return tiers.filter((t) => t <= maxCredits);
}

function formatCredits(n) {
  if (n >= 1000) return `${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  return n.toLocaleString();
}

export default function TopupPage({ billing, onBillingChange }) {
  const navigate = useNavigate();
  const [creditsWanted, setCreditsWanted] = useState(0);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState(null);

  // Server-trusted numbers
  const ratePer1kCents = billing?.topup_cost_per_1k_credits ?? 0;
  const maxCredits = billing?.topup_max_credits ?? 0;
  const eligible = !!billing?.topup_eligible;
  const reason = billing?.topup_reason;
  const planCode = billing?.plan_code || 'free';
  const creditsLeft = billing?.credit_balance ?? 0;

  // Minimum credits that produces the $15 NOWPayments floor.
  // Capped to maxCredits so small plans (where max < $15 worth) still
  // work — the user just gets whatever fits.
  const minCreditsRaw =
    ratePer1kCents > 0
      ? Math.max(1, Math.ceil((NOWPAYMENTS_MIN_USD_CENTS * 1000) / ratePer1kCents))
      : 1;
  const minCredits = Math.min(minCreditsRaw, Math.max(1, maxCredits));

  // Initialize slider to half of the user's max once we know it.
  useEffect(() => {
    if (maxCredits > 0 && creditsWanted === 0) {
      setCreditsWanted(Math.max(minCredits, Math.round(maxCredits / 2)));
    }
  }, [maxCredits, minCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  const credits = eligible
    ? Math.min(Math.max(creditsWanted, minCredits), maxCredits)
    : 0;
  const usdCents =
    ratePer1kCents > 0 ? Math.ceil((credits * ratePer1kCents) / 1000) : 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await apiFetch('/payments/history').catch(() => ({ payments: [] }));
      setPayments(h.payments || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function startCheckout() {
    if (!eligible || credits <= 0 || credits > maxCredits) return;
    setCheckout({ kind: 'topup', credits, usdCents });
  }

  // Only topups in history
  const topups = payments.filter((p) => p.kind === 'topup');
  // Phase-16: pagination for the recent topups list (5 per page).
  const topupsPagination = useLoadMore(topups, 5);

  const quickPresets = deriveQuickCreditPresets(maxCredits);

  return (
    <div className="space-y-6 sm:space-y-8">
      <SectionHeader
        title="Top up credits"
        description="Buy credits without changing your subscription. Crypto only (NOWPayments)."
      />

      {/* Eligibility banner — server-authoritative */}
      <TopupGateBanner
        eligible={eligible}
        reason={reason}
        planCode={planCode}
        creditsLeft={creditsLeft}
        maxCredits={maxCredits}
      />

      {/* Two-column: form (2/3) + sidebar (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Top-up form */}
        <Card className="lg:col-span-2 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand/8 blur-3xl pointer-events-none" />
          <Card.Header>
            <div>
              <Card.Title>Buy credits</Card.Title>
              <Card.Subtitle>
                {ratePer1kCents > 0
                  ? <>1,000 credits = <span className="text-fg font-medium">${(ratePer1kCents / 100).toFixed(2)}</span>. Up to <span className="text-fg font-medium">{formatNumber(maxCredits)}</span> per top-up.</>
                  : 'Loading pricing…'}
              </Card.Subtitle>
            </div>
            <Wallet className="h-5 w-5 text-fg-subtle" />
          </Card.Header>
          <Card.Body className="space-y-6">
            {/* Slider — picks CREDITS, not dollars */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-xs font-medium text-fg-muted uppercase tracking-wider">
                  Credits
                </div>
                <div className="text-2xl font-semibold text-fg tabular-nums">
                  {formatNumber(credits)}
                </div>
              </div>
              <input
                type="range"
                min={eligible ? minCredits : 0}
                max={eligible ? maxCredits : Math.max(1, maxCredits)}
                step={Math.max(1, Math.round((minCreditsRaw || 1) / 10))}
                value={credits}
                disabled={!eligible}
                onChange={(e) => setCreditsWanted(Number(e.target.value))}
                className={cn(
                  'w-full h-2 rounded-full appearance-none cursor-pointer accent-brand',
                  eligible ? 'bg-surface-2' : 'bg-surface-3 opacity-50 cursor-not-allowed'
                )}
                style={eligible ? {
                  background: `linear-gradient(to right, hsl(var(--brand)) 0%, hsl(var(--brand)) ${((credits - minCredits) / Math.max(1, maxCredits - minCredits)) * 100}%, hsl(var(--surface-2)) ${((credits - minCredits) / Math.max(1, maxCredits - minCredits)) * 100}%, hsl(var(--surface-2)) 100%)`,
                } : undefined}
              />
              <div className="flex justify-between text-2xs text-fg-subtle mt-2 tabular-nums">
                <span>{formatCredits(minCredits)}</span>
                <span>{formatCredits(maxCredits)}</span>
              </div>
            </div>

            {/* Quick presets — credit amounts scaled to max */}
            {quickPresets.length > 0 && (
              <div>
                <div className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-2">
                  Quick amounts
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {quickPresets.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditsWanted(amount)}
                      disabled={!eligible}
                      className={cn(
                        'px-2 py-2 text-xs font-medium rounded-md border transition-colors',
                        credits === amount
                          ? 'bg-brand/10 border-brand/40 text-brand'
                          : 'bg-surface-2 border-border text-fg-muted hover:text-fg hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed'
                      )}
                    >
                      {formatCredits(amount)}
                    </button>
                  ))}
                  <button
                    onClick={() => setCreditsWanted(maxCredits)}
                    disabled={!eligible}
                    className={cn(
                      'px-2 py-2 text-xs font-medium rounded-md border transition-colors',
                      credits === maxCredits
                        ? 'bg-brand/10 border-brand/40 text-brand'
                        : 'bg-surface-2 border-border text-fg-muted hover:text-fg hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed'
                    )}
                  >
                    Max
                  </button>
                </div>
              </div>
            )}

            {/* You get / You pay box */}
            <div className={cn(
              'rounded-lg border p-5',
              eligible
                ? 'border-border bg-gradient-to-br from-brand/5 to-surface-2'
                : 'border-border bg-surface-2/40'
            )}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs text-fg-muted">You get</div>
                  <div className="mt-1 text-3xl font-semibold text-fg tabular-nums">
                    {formatNumber(credits)}
                  </div>
                  <div className="text-xs text-fg-muted">credits</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-fg-muted">You pay</div>
                  <div className="mt-1 text-2xl font-semibold text-fg tabular-nums">
                    {usdCents > 0 ? formatUSD(usdCents) : '—'}
                  </div>
                  <div className="text-xs text-fg-muted">via crypto</div>
                </div>
              </div>
            </div>

            <Button
              variant="primary"
              size="md"
              className="w-full"
              disabled={!eligible || credits <= 0}
              onClick={startCheckout}
            >
              <Wallet className="h-4 w-4" />
              Buy {formatNumber(credits)} credits for {usdCents > 0 ? formatUSD(usdCents) : '—'}
              <ArrowRight className="h-4 w-4" />
            </Button>

            <p className="text-2xs text-fg-subtle text-center">
              Crypto only — Bitcoin, Ethereum, USDT, and 100+ other coins.
              Credits appear in your account automatically once the payment confirms.
            </p>
          </Card.Body>
        </Card>

        {/* Right sidebar: balance + pricing + recent topups */}
        <div className="space-y-4">
          {/* Balance card */}
          <Card>
            <Card.Header>
              <div>
                <Card.Title>Your balance</Card.Title>
              </div>
              <Dot variant={creditsLeft > 0 ? 'success' : 'destructive'} pulse />
            </Card.Header>
            <Card.Body>
              <div className="text-4xl font-semibold text-fg tabular-nums">
                {formatNumber(creditsLeft)}
              </div>
              <div className="text-xs text-fg-muted mt-1">credits available now</div>
              {maxCredits > 0 && (
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-fg-muted">Max per top-up</span>
                    <span className="text-fg tabular-nums">{formatNumber(maxCredits)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-fg-muted">Per 1k credits</span>
                    <span className="text-fg tabular-nums">
                      {ratePer1kCents > 0 ? `$${(ratePer1kCents / 100).toFixed(2)}` : '—'}
                    </span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Per-call pricing mini (compact) */}
          <PerCallMini perCallCosts={billing?.per_call_costs} />
        </div>
      </div>

      {/* Topup history */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>Top-up history</Card.Title>
            <Card.Subtitle>Your most recent credit purchases.</Card.Subtitle>
          </div>
          <Link
            to="/billing"
            className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            See subscriptions
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
          ) : topups.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Receipt}
                title="No top-ups yet"
                description="Buy credits above and they'll show up here."
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {topupsPagination.visible.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-5 py-3.5 text-sm">
                  <Clock className="h-3.5 w-3.5 text-fg-subtle shrink-0" />
                  <span className="text-2xs text-fg-muted tabular-nums w-20 shrink-0">
                    {p.created_at ? formatRelative(p.created_at) : '—'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-fg">{formatNumber(p.credits || 0)} credits</span>
                    {p.pay_currency && (
                      <span className="ml-2 text-2xs text-fg-subtle">
                        via {p.pay_currency.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-fg-muted tabular-nums">
                    {formatUSD(p.amount_cents)}
                  </span>
                  <Badge variant={p.status === 'finished' ? 'success' : p.status === 'failed' ? 'destructive' : 'warning'}>
                    {p.status === 'finished' ? 'Paid' : p.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          {!loading && topups.length > 0 && (
            <div className="border-t border-border p-3">
              <LoadMore
                hasMore={topupsPagination.hasMore}
                onLoadMore={topupsPagination.loadMore}
                visibleCount={topupsPagination.visibleCount}
                total={topupsPagination.total}
                pageSize={5}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Checkout dialog */}
      <CheckoutDialog
        open={!!checkout}
        kind={checkout?.kind}
        planCode={null}
        planName="Top up credits"
        usdCents={checkout?.usdCents || 0}
        onClose={() => { setCheckout(null); onBillingChange?.(); }}
        onSuccess={() => { setCheckout(null); onBillingChange?.(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact per-call pricing reference (shown on the topup sidebar)
// ---------------------------------------------------------------------------

// Phase-1+: gate banner. Pure presentation now — all eligibility logic
// comes from the server payload. Three states:
//   - eligible (topup_eligible=true): green/positive banner
//   - blocked, has-credits: amber/pause banner with counts
//   - blocked, no-credits (free plan, or paid but cost missing): amber
function TopupGateBanner({ eligible, reason, planCode, creditsLeft, maxCredits }) {
  if (eligible) {
    return (
      <div className="px-4 py-3 rounded-lg border border-success/30 bg-success/5 text-sm text-success flex items-center gap-2">
        <Check className="h-4 w-4 shrink-0" />
        <span>Top up is unlocked — your balance is at 0. Buy more below.</span>
      </div>
    );
  }
  if (planCode === 'free') {
    return (
      <div className="px-4 py-3 rounded-lg border border-warning/30 bg-warning/5 text-sm text-warning">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Top-up is not available on the free plan.</div>
            <div className="opacity-80 mt-0.5">
              Upgrade to a paid plan to access top-ups.{' '}
              <Link to="/billing" className="underline">See plans →</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Paid user with credits remaining — locked.
  return (
    <div className="px-4 py-3 rounded-lg border border-warning/30 bg-warning/5 text-sm text-warning">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">Top up is paused while you have plan credits.</div>
          <div className="opacity-80 mt-0.5">
            You have <strong>{formatNumber(creditsLeft)}</strong> credits left.
            Top up unlocks automatically when your balance reaches 0
            {maxCredits > 0 ? <> (you can buy up to <strong>{formatNumber(maxCredits)}</strong> per top-up)</> : null}.
          </div>
        </div>
      </div>
    </div>
  );
}

function PerCallMini({ perCallCosts }) {
  const c = {
    classic_base: 1,
    stealth_base: 4,
    js_render_extra: 1,
    screenshot_extra: 2,
    pdf_extra: 1,
    ...(perCallCosts || {}),
  };
  const items = [
    { icon: Zap, label: 'Classic', cost: c.classic_base, tone: 'text-fg-muted' },
    { icon: Zap, label: 'Stealth', cost: c.stealth_base, tone: 'text-warning' },
    { icon: Code2, label: '+ JS', cost: `+${c.js_render_extra}`, tone: 'text-fg-muted' },
    { icon: Camera, label: '+ Shot', cost: `+${c.screenshot_extra}`, tone: 'text-fg-muted' },
    { icon: FileText, label: '+ PDF', cost: `+${c.pdf_extra}`, tone: 'text-fg-muted' },
  ];
  return (
    <Card>
      <Card.Header>
        <div>
          <Card.Title>Per-call cost</Card.Title>
          <Card.Subtitle>What each request costs you.</Card.Subtitle>
        </div>
        <Link
          to="/docs"
          className="inline-flex items-center justify-center h-5 w-5 rounded-full text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
          title="Credit cost depends on the engine that ran + extras. See more in the docs."
          aria-label="See more in docs"
        >
          <Info className="h-3.5 w-3.5" />
        </Link>
      </Card.Header>
      <Card.Body className="space-y-2">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.label} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Icon className={cn('h-3.5 w-3.5', it.tone)} />
                <span className="text-fg-muted">{it.label}</span>
              </div>
              <span className="text-fg font-medium tabular-nums">{it.cost}</span>
            </div>
          );
        })}
        <p className="text-2xs text-fg-subtle pt-2 mt-2 border-t border-border">
          Failed requests still cost — no refunds.
        </p>
      </Card.Body>
    </Card>
  );
}
